import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import "./Appointments.css";
import PageHeader from "./layout/PageHeader";
import TimeInputWithAmPm from "./TimeInputWithAmPm";
import SearchableSelect from "./SearchableSelect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarPlus,
  faFilter,
  faClock,
  faTimes,
  faRedoAlt,
  faUserNurse,
  faUserFriends,
  faCheckCircle,
  faSignOutAlt,
  faNotesMedical,
  faClipboardCheck,
  faClipboardList,
  faTrash,
  faMapMarkerAlt,
  faRobot,
  faMicrophone,
} from "@fortawesome/free-solid-svg-icons";
import { Calendar } from "lucide-react";
import {
  fetchAppointments,
  createAppointment,
  updateAppointment,
  deleteAppointment,
  fetchAddressSuggestions,
  fetchClients,
  fetchUsers,
  fetchQuestionSets,
  fetchClient,
  getCanarySubject,
} from "../utils/api";
import dayjs from "dayjs";
import {
  GoogleMap,
  useJsApiLoader,
} from "@react-google-maps/api";

const combineDateTime = (dateStr, timeStr, timezone) => {
  if (!dateStr || !timeStr) return null;
  try {
    // Timezone parameter kept for future use; currently relies on local time
    return dayjs(`${dateStr} ${timeStr}`).toDate();
  } catch (e) {
    console.warn("Failed to combine date/time", e);
    return null;
  }
};

const libraries = ["places"];

const mapContainerStyle = {
  width: "100%",
  height: "100%",
  borderRadius: "12px",
};

function getDerivedStatus(appointment) {
  const status = String(appointment?.status || "").trim().toLowerCase();
  const start = dayjs(appointment?.start);
  const hasCheckIn = Boolean(appointment?.checkInTime);
  const pendingLike =
    status === "pending" || status === "accepted" || status === "starting soon";
  const isTerminal =
    status === "checked in" ||
    status === "checked_in" ||
    status === "checked out" ||
    status === "checked_out" ||
    status === "completed" ||
    status === "cancelled" ||
    status === "canceled" ||
    status === "rejected" ||
    status === "delayed";

  if (
    appointment.status === "Checked In" ||
    appointment.status === "checked in" ||
    appointment.status === "checked_in"
  ) {
    return "In Progress";
  }

  // Show "Hold" immediately when a pending/accepted/starting-soon schedule has
  // already passed start time and has not been checked-in yet. This flags it for
  // manual fix and avoids treating it like an active assignment.
  if (!isTerminal && pendingLike && !hasCheckIn && start.isValid() && start.isBefore(dayjs())) {
    return "Hold";
  }

  return appointment.status;
}

function getStatusColor(appointment) {
  const derivedStatus = getDerivedStatus(appointment);

  if (derivedStatus === "approved" || derivedStatus === "Approved") {
    const now = new Date();
    const end = new Date(appointment.end);
    if (now > end) return "#22c55e"; // green
  }

  if (derivedStatus === "Hold" || derivedStatus === "hold") return "#f97316"; // orange
  if (derivedStatus === "In Progress") return "#22c55e"; // green in-progress
  if (derivedStatus === "Checked In") return "#0ea5e9"; // legacy

  return ""; // let CSS handle default
}

function isLateCheckIn(appt) {
  if (!appt?.checkInTime || !appt?.start) return false;
  return dayjs(appt.checkInTime).isAfter(dayjs(appt.start).add(24, "hour"));
}

function parseAvailabilityString(availStr) {
  if (!availStr) return [];
  const parts = availStr
    .split(";")
    .map((p) => p.trim())
    .filter(Boolean);
  const slots = [];
  const regex =
    /(?<day>Mon|Tue|Wed|Thu|Fri|Sat|Sun)\s+(?<start>\d{2}:\d{2})-(?<end>\d{2}:\d{2})/;
  parts.forEach((part) => {
    const m = part.match(regex);
    if (m?.groups) {
      slots.push({
        day: m.groups.day,
        start: m.groups.start,
        end: m.groups.end,
      });
    }
  });
  return slots;
}

const statusChips = [
  { value: "All Status", label: "All Status", icon: "⚪" },
  { value: "Checked In", label: "In Progress", icon: "🟢" },
  { value: "Starting Soon", label: "Starting Soon", icon: "🟠" },
  { value: "Checked Out", label: "Checked Out", icon: "🟣" },
  { value: "Delayed", label: "Delayed", icon: "🔴" },
  { value: "Pending", label: "Pending", icon: "🟡" },
  { value: "Accepted", label: "Accepted", icon: "🟢" },
  { value: "Rejected", label: "Rejected", icon: "🔴" },
  { value: "Cancelled", label: "Cancelled", icon: "⚫️" },
  { value: "Completed", label: "Completed", icon: "🔵" },
];

const providerTypes = [
  { value: "All Providers", label: "All Providers", icon: "⚪" },
  { value: "Nurse", label: "Nurse", icon: "🩺" },
  { value: "Caregiver", label: "Caregiver", icon: "🤝" },
];
const recurrences = [
  "All",
  "One-time",
  "Weekly",
  "Bi-weekly",
  "Monthly",
  "Quarterly",
];
const RECURRENCE_OCCURRENCES = 4;

// Mock Data for Answers
const MOCK_CHECK_IN = [
  {
    id: 1,
    question: "How is the patient feeling today?",
    type: "Single",
    answer: "Excellent",
  },
  {
    id: 2,
    question: "Has the patient taken their morning medications?",
    type: "YesNo",
    answer: "Yes",
  },
];

const MOCK_CHECK_OUT = [
  {
    id: 1,
    question: "Was the care plan completed successfully?",
    type: "YesNo",
    answer: "Yes",
  },
  {
    id: 2,
    question: "Services provided during this visit",
    type: "Multiple",
    answer: ["Vital signs monitoring", "Medication administration"],
  },
  {
    id: 3,
    question: "Patient's condition at the end of visit",
    type: "Single",
    answer: "Stable",
  },
];

// Version 5 - Guarded ID
const Appointments = ({
  activeLocation,
  locations = [],
  onChangeLocation,
  companyDisplayName,
  currentUser,
  onLogout,
  onLock,
}) => {
  console.log("🚀 Appointments component loaded - Version 5 (ID Guard)");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [providerFilter, setProviderFilter] = useState("All Providers");
  const [recurrenceFilter, setRecurrenceFilter] = useState("All");
  const SCHEDULE_LOOKAHEAD_DAYS = 30;
  const [statFilter, setStatFilter] = useState("upcoming30");
  const [search, setSearch] = useState("");
  const [appointments, setAppointments] = useState([]);
  // Bucket counts from the backend, computed over the FULL date window with
  // no result cap — the fetched list may be truncated at the server's safety
  // cap, so stat cards must not be derived from it when these are available.
  const [serverStats, setServerStats] = useState(null);
  const [activeAppointment, setActiveAppointment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [geocodeWarning, setGeocodeWarning] = useState("");
  const [voiceProvisionWarning, setVoiceProvisionWarning] = useState("");
  const [, setSelectedMarker] = useState(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const infoWindowRef = useRef(null);

  // Guard against concurrent duplicate fetches (same pattern as
  // handledEventFromUrlRef in AlbertAiAlertsPage). If a legitimate refetch
  // trigger (filter/location change) fires while a request is in flight, we
  // queue exactly one trailing refetch instead of suppressing it.
  const fetchInFlightRef = useRef(false);
  const refetchQueuedRef = useRef(false);
  const loadAppointmentsRef = useRef(null);

  const loadAppointments = useCallback(async () => {
    if (fetchInFlightRef.current) {
      refetchQueuedRef.current = true;
      return;
    }
    fetchInFlightRef.current = true;
    setLoading(true);
    setError("");
    try {
      // Guard: require active location to avoid bad requests / CORS noise
      const locId = activeLocation?.id || activeLocation?._id;
      if (!locId) {
        setAppointments([]);
        setClientsList([]);
        setActiveAppointment(null);
        setLoading(false);
        setError("Select a location to view appointments");
        return;
      }

      // 🛑 CRITICAL FIX: Validate Location ID format to prevent backend 503 crashes
      // A known issue causes malformed IDs (e.g. 29 chars) to be passed during navigation.
      // Standard MongoDB ObjectIDs are exactly 24 hex characters.
      const isValidObjectId = /^[0-9a-fA-F]{24}$/.test(String(locId));
      if (!isValidObjectId) {
        console.error(
          "🚨 CRITICAL: Invalid Location ID detected:",
          locId,
          "Length:",
          String(locId).length,
        );
        setError(
          `Invalid Location ID detected. Please switch locations or refresh the page.`,
        );
        setLoading(false);
        return;
      }

      const filters = {
        status: statusFilter,
        providerRole: providerFilter,
        recurrence: recurrenceFilter,
        q: search || undefined,
        locationId: locId,
      };
      const payload = await fetchAppointments({
        ...filters,
        includeStats: true,
      });
      // { appointments, stats } with includeStats; tolerate a plain array
      // (e.g. older backend during deploy skew).
      const data = Array.isArray(payload)
        ? payload
        : payload?.appointments || [];
      setServerStats(Array.isArray(payload) ? null : payload?.stats || null);
      setAppointments(data);
      // Functional update so `activeAppointment` is NOT needed in this
      // callback's deps — having it there made every fetch change the
      // callback identity and re-trigger the fetch effect (duplicate calls).
      if (data && data.length > 0) {
        setActiveAppointment((prev) => prev || data[0]);
      } else {
        setActiveAppointment(null);
      }

      const [clientsData, usersData] = await Promise.all([
        fetchClients(locId, ""),
        fetchUsers(),
      ]);
      setClientsList(clientsData || []);

      console.log("📋 Loaded clients:", clientsData?.length, "clients");

      // 🔐 SECURITY: Log providers loaded for tenant isolation verification
      const currentUserCompanyId = JSON.parse(
        localStorage.getItem("user") || "{}",
      )?.companyId;

      if (usersData && usersData.length > 0) {
        // 🔐 SECURITY WARNING: Check for tenant isolation breach
        const mismatchedUsers = usersData.filter(
          (u) => u.companyId && u.companyId !== currentUserCompanyId,
        );
        if (mismatchedUsers.length > 0) {
          console.error(
            "🚨 [SECURITY BREACH] USERS FROM OTHER COMPANIES DETECTED!",
            mismatchedUsers.map((u) => ({
              name: u.name,
              companyId: u.companyId,
              accountType: u.accountType,
            })),
          );
        } else {
          console.log(
            "   ✅ All users belong to current company - tenant isolation OK",
          );
        }
      }

      const providerCandidates = (usersData || []).filter((u) => {
        const acct = String(u.accountType || "").toLowerCase();
        return acct.includes("nurse") || acct.includes("caregiver");
      });

      const uniqueById = [];
      const seen = new Set();
      providerCandidates.forEach((u) => {
        const uid = u.id || u._id;
        if (uid && !seen.has(uid)) {
          seen.add(uid);
          uniqueById.push(u);
        }
      });

      setProvidersList(
        [
          { id: "albert-ai-nurse", name: "AlbertAi", accountType: "Nurse" },
          ...uniqueById,
        ].filter(Boolean),
      );
    } catch (err) {
      console.error("Failed to load appointments:", err);
      // Don't show error to user if it's just a cancelled request or similar transient issue
      if (err.message !== "canceled") {
        setError(err.message || "Failed to load appointments");
      }
    } finally {
      fetchInFlightRef.current = false;
      setLoading(false);
      if (refetchQueuedRef.current) {
        refetchQueuedRef.current = false;
        // Re-run once with the latest filters (via ref, so we always call
        // the newest callback identity rather than this stale closure).
        loadAppointmentsRef.current?.();
      }
    }
  }, [
    activeLocation?._id,
    activeLocation?.id,
    providerFilter,
    recurrenceFilter,
    search,
    statusFilter,
  ]);

  useEffect(() => {
    loadAppointmentsRef.current = loadAppointments;
  }, [loadAppointments]);
  const buildClientAddress = (client) => {
    if (!client) return "";
    if (client.address) return client.address;
    const parts = [client.street, client.city, client.state, client.zip].filter(
      Boolean,
    );
    return parts.join(", ").trim();
  };
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressQuery, setAddressQuery] = useState("");
  const addressQueryRef = useRef("");
  const addressTimer = useRef(null);
  const geocodeAttemptRef = useRef(null);
  // Modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCheckInModalOpen, setIsCheckInModalOpen] = useState(false);
  const [isCheckOutModalOpen, setIsCheckOutModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteMessage, setDeleteMessage] = useState("");
  const [checkInSets, setCheckInSets] = useState([]);
  const [checkOutSets, setCheckOutSets] = useState([]);
  const [checkInQuestionSetId, setCheckInQuestionSetId] = useState("");
  const [checkOutQuestionSetId, setCheckOutQuestionSetId] = useState("");

  const [editId, setEditId] = useState(null);
  const [clientsList, setClientsList] = useState([]);
  const [form, setForm] = useState({
    client: "",
    clientId: "",
    clientPhone: "",
    provider: "",
    providerId: "",
    providerRole: "Nurse",
    providerAvailability: "available",
    status: "Pending",
    recurrence: "One-time",
    // Draft rows (one appointment per row)
    appointmentsDraft: [
      {
        date: dayjs().format("YYYY-MM-DD"),
        startTime: "09:00",
        durationMin: 60,
      },
    ],
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    // Legacy fields kept for edit mode compatibility
    start: "",
    end: "",
    address: "",
    lat: "",
    lng: "",
    notes: "",
    showCheckInQuestions: true,
    showCheckOutQuestions: true,
  });

  const handleFormChange = useCallback((field, value) => {
    setForm((prev) => {
      const updated = { ...prev, [field]: value };
      return updated;
    });
    setFieldErrors((errs) => {
      if (!errs?.[field] || !value) return errs;
      const next = { ...errs };
      delete next[field];
      return next;
    });
  }, []);

  const handleClientSelect = useCallback(
    async (cid) => {
      setError("");
      let client = clientsList.find((c) => (c.id || c._id) === cid);

      // If we have no phone/address locally, fetch full client record
      const hasContact =
        client &&
        (client.phone ||
          client.address ||
          client.street ||
          client.city ||
          client.state ||
          client.zip);
      if (cid && !hasContact) {
        try {
          const fresh = await fetchClient(cid);
          if (fresh) client = fresh;
        } catch (err) {
          console.warn("Failed to fetch client details", err);
        }
      }

      handleFormChange("clientId", cid);
      handleFormChange(
        "client",
        client
          ? `${client.firstName || ""} ${client.lastName || ""}`.trim()
          : form.client,
      );
      if (client?.phone) {
        handleFormChange("clientPhone", client.phone);
      }
      const populatedAddress = buildClientAddress(client);
      if (populatedAddress) {
        handleFormChange("address", populatedAddress);
        setAddressQuery(populatedAddress);
        // Clear any previous address validation errors
        setFieldErrors((prev) => {
          const next = { ...prev };
          delete next.address;
          return next;
        });
      }
    },
    [
      clientsList,
      form.client,
      handleFormChange,
      setAddressQuery,
      setFieldErrors,
      setError,
    ],
  );

  const [providersList, setProvidersList] = useState([]);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [providerAvailabilityInfo, setProviderAvailabilityInfo] = useState([]);
  const [availabilityWarning, setAvailabilityWarning] = useState("");

  const GOOGLE_MAPS_KEY =
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    process.env.REACT_APP_GOOGLE_API_KEY ||
    "";

  const loadAddressSuggestions = useCallback(
    async (query) => {
      if (!GOOGLE_MAPS_KEY) {
        setAddressSuggestions([]);
        return;
      }
      const trimmed = (query || "").trim();
      if (!trimmed) {
        setAddressSuggestions([]);
        return;
      }
      const currentQuery = trimmed;
      addressQueryRef.current = currentQuery;
      try {
        const suggestions = await fetchAddressSuggestions(currentQuery);
        const usOnly =
          (suggestions || []).filter(
            (s) =>
              s.display_name?.toLowerCase().includes("united states") ||
              s.address?.country_code === "us",
          ) || [];
        if (addressQueryRef.current !== currentQuery) return;
        setAddressSuggestions(usOnly.slice(0, 12));
      } catch (err) {
        console.warn("Address lookup failed", err);
      }
    },
    [GOOGLE_MAPS_KEY],
  );

  // Only load Google Maps if API key exists
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_KEY,
    libraries,
    preventGoogleFontsLoading: true,
  });

  useEffect(() => {
    const loadQuestionSets = async () => {
      try {
        const [checkIn, checkOut] = await Promise.all([
          fetchQuestionSets({ type: "CHECK_IN" }),
          fetchQuestionSets({ type: "CHECK_OUT" }),
        ]);
        setCheckInSets(checkIn || []);
        setCheckOutSets(checkOut || []);
      } catch (err) {
        console.error("Failed to load question sets", err);
      }
    };
    loadQuestionSets();
  }, []);

  // Auto-refresh appointments every 60 seconds to keep status live
  useEffect(() => {
    loadAppointments();
    const intervalId = setInterval(() => {
      loadAppointments();
    }, 60_000);
    return () => clearInterval(intervalId);
  }, [loadAppointments]);

  // Explicit marker creation function
  const showLocationOnMap = useCallback((appointment) => {
    if (!mapInstanceRef.current || !window.google) {
      console.warn("⚠️ Map or Google Maps not ready");
      return;
    }

    const lat = Number(appointment.coords?.lat);
    const lng = Number(appointment.coords?.lng);

    if (!lat || !lng) {
      console.warn("⚠️ No valid coordinates for appointment:", appointment.id);
      return;
    }

    console.log("🧷 Creating marker at:", {
      lat,
      lng,
      client: appointment.clientName,
    });

    // Remove previous marker
    if (markerRef.current) {
      console.log("🗑️ Removing previous marker");
      markerRef.current.setMap(null);
      markerRef.current = null;
    }

    // Remove previous info window
    if (infoWindowRef.current) {
      infoWindowRef.current.close();
      infoWindowRef.current = null;
    }

    // Create new marker
    markerRef.current = new window.google.maps.Marker({
      position: { lat, lng },
      map: mapInstanceRef.current,
      title: appointment.clientName || appointment.client || "Appointment",
      animation: window.google.maps.Animation.DROP,
    });

    console.log("✅ Marker created successfully");

    // Clear geocoding warning since marker was successfully placed
    setGeocodeWarning("");

    // Create info window
    const contentString = `
      <div style="padding: 8px; min-width: 200px;">
        <h3 style="margin: 0 0 8px 0; fontSize: 16px; font-weight: 600;">
          ${appointment.clientName || appointment.client || "Client"}
        </h3>
        <p style="margin: 4px 0; font-size: 14px; color: #666;">
          ${appointment.address || "No address"}
        </p>
        <p style="margin: 4px 0; font-size: 12px; color: #999;">
          📅 ${
            appointment.start
              ? new Date(appointment.start).toLocaleString()
              : "No date"
          }
        </p>
      </div>
    `;

    infoWindowRef.current = new window.google.maps.InfoWindow({
      content: contentString,
    });

    // Add click listener to marker
    markerRef.current.addListener("click", () => {
      console.log("📍 Marker clicked");
      infoWindowRef.current.open(mapInstanceRef.current, markerRef.current);
    });

    // Center and zoom map
    mapInstanceRef.current.setCenter({ lat, lng });
    mapInstanceRef.current.setZoom(15);
    console.log("🎯 Map centered and zoomed to marker");
  }, []);

  // Smooth map transition when activeAppointment changes
  useEffect(() => {
    if (
      mapInstanceRef.current &&
      activeAppointment?.coords?.lat &&
      activeAppointment?.coords?.lng
    ) {
      showLocationOnMap(activeAppointment);
    }
  }, [activeAppointment, showLocationOnMap]);

  // Auto-geocode on view if appointment has address but no coords (helps old records)
  useEffect(() => {
    const geocodeIfNeeded = async () => {
      const apptId = activeAppointment?.id;
      if (
        !GOOGLE_MAPS_KEY ||
        !apptId ||
        !activeAppointment?.address ||
        (activeAppointment?.coords?.lat && activeAppointment?.coords?.lng) ||
        geocodeAttemptRef.current === apptId
      ) {
        return;
      }
      geocodeAttemptRef.current = apptId;
      try {
        console.log("🔍 Auto-geocoding address:", activeAppointment.address);
        const results = await fetchAddressSuggestions(
          activeAppointment.address,
        );
        console.log("📍 Auto-geocoding results:", results);
        if (results && results.length > 0) {
          const best = results[0];
          const lat = Number(best.lat);
          const lng = Number(best.lon);
          if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
            console.log("✅ Auto-geocoding successful:", {
              lat,
              lng,
              formatted: best.display_name,
            });
            await updateAppointment(apptId, {
              address: activeAppointment.address,
              coords: { lat, lng },
            });
            setActiveAppointment((prev) =>
              prev && prev.id === apptId
                ? { ...prev, coords: { lat, lng } }
                : prev,
            );
            // Also update list so reselecting same item keeps coords
            setAppointments((prev) =>
              (prev || []).map((a) =>
                a.id === apptId ? { ...a, coords: { lat, lng } } : a,
              ),
            );
            // Clear warning on successful geocoding
            setGeocodeWarning("");
          } else {
            console.error("❌ Auto-geocoding: Invalid coordinates:", best);
            setGeocodeWarning(
              "Could not determine valid coordinates from address.",
            );
          }
        } else {
          console.warn(
            "⚠️ Auto-geocoding: No results for:",
            activeAppointment.address,
          );
          setGeocodeWarning(
            "Unable to geocode address. Appointment saved without map location.",
          );
        }
      } catch (err) {
        console.warn("❌ Auto-geocode failed:", err);
        setGeocodeWarning(
          "Geocoding service unavailable. Appointment saved without map location.",
        );
      }
    };
    geocodeIfNeeded();
  }, [activeAppointment, GOOGLE_MAPS_KEY]);

  const filteredProviders = useMemo(() => {
    if (!providersList) return [];
    // Filter by selected providerRole (Nurse/Caregiver)
    return providersList.filter((p) => {
      const acct = String(p.accountType || "").toLowerCase();
      const role = String(form.providerRole || "").toLowerCase();
      if (!role) return true; // if not selected yet, show nothing? We'll handle empty below
      return acct.includes(role);
    });
  }, [providersList, form.providerRole]);

  useEffect(() => {
    if (!isModalOpen) return;
    let active = true;
    const refreshProviders = async () => {
      try {
        const usersData = await fetchUsers();
        if (!active) return;
        const providerCandidates = (usersData || []).filter((u) => {
          const acct = String(u.accountType || "").toLowerCase();
          return acct.includes("nurse") || acct.includes("caregiver");
        });

        const uniqueById = [];
        const seen = new Set();
        providerCandidates.forEach((u) => {
          const uid = u.id || u._id;
          if (uid && !seen.has(uid)) {
            seen.add(uid);
            uniqueById.push(u);
          }
        });

        setProvidersList(
          [
            { id: "albert-ai-nurse", name: "AlbertAi", accountType: "Nurse" },
            ...uniqueById,
          ].filter(Boolean),
        );
      } catch (err) {
        if (!active) return;
        console.warn("Failed to refresh providers", err);
      }
    };
    refreshProviders();
    return () => {
      active = false;
    };
  }, [isModalOpen]);

  const getProviderName = useCallback(
    (appt) => {
      const pid =
        (appt.providerId &&
          (appt.providerId.id || appt.providerId._id || appt.providerId)) ||
        appt.providerId ||
        "";
      const found = filteredProviders.find(
        (p) => (p.id || p._id || p.name) === pid,
      );
      return appt.providerName || appt.provider || found?.name || "No provider";
    },
    [filteredProviders],
  );

  const filteredAppointments = useMemo(() => {
    const now = dayjs();
    return (appointments || []).filter((a) => {
      const statusLower = String(a?.status || "").toLowerCase();
      const isResolved =
        statusLower === "checked out" ||
        statusLower === "completed" ||
        statusLower === "cancelled" ||
        statusLower === "canceled" ||
        statusLower === "rejected";

      const passedDay =
        !isResolved &&
        a?.start &&
        dayjs(a.start).isValid() &&
        now.diff(dayjs(a.start), "day") > 1;

      // Move older-than-1-day schedules into their own tab
      if (statFilter === "passed") {
        if (!passedDay) return false;
      } else {
        if (passedDay) return false;
      }

      if (search) {
        const q = search.toLowerCase();
        if (
          !(
            (a.clientName || a.client || "").toLowerCase().includes(q) ||
            getProviderName(a).toLowerCase().includes(q)
          )
        )
          return false;
      }
      if (statusFilter !== "All Status" && a.status !== statusFilter)
        return false;
      if (
        providerFilter !== "All Providers" &&
        a.providerRole !== providerFilter
      )
        return false;
      if (recurrenceFilter !== "All" && a.recurrence !== recurrenceFilter)
        return false;
      if (statFilter === "upcoming30") {
        if (!a.start || !dayjs(a.start).isValid()) return false;
        const start = dayjs(a.start);
        if (start.isBefore(now.startOf("day"))) return false;
        if (start.isAfter(now.add(SCHEDULE_LOOKAHEAD_DAYS, "day").endOf("day"))) {
          return false;
        }
      } else if (statFilter === "today") {
        if (!a.start || !dayjs(a.start).isSame(now, "day")) return false;
      } else if (statFilter === "soon") {
        if (!a.start || !dayjs(a.start).isAfter(now)) return false;
        if (dayjs(a.start).diff(now, "minute") > 480) return false;
      } else if (statFilter === "checkedin" && a.status !== "Checked In") {
        return false;
      } else if (statFilter === "checkedout" && a.status !== "Checked Out") {
        return false;
      } else if (statFilter === "delayed" && a.status !== "Delayed") {
        return false;
      }
      return true;
    });
  }, [
    appointments,
    search,
    statusFilter,
    providerFilter,
    recurrenceFilter,
    statFilter,
    getProviderName,
  ]);

  const pageSize = 20;
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.max(
    1,
    Math.ceil(filteredAppointments.length / pageSize),
  );
  const pagedAppointments = useMemo(
    () =>
      filteredAppointments.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize,
      ),
    [filteredAppointments, currentPage],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [
    search,
    statusFilter,
    providerFilter,
    recurrenceFilter,
    statFilter,
    appointments.length,
  ]);
  const derivedStats = useMemo(() => {
    const now = dayjs();
    const isPassedDay = (a) => {
      const statusLower = String(a?.status || "").toLowerCase();
      const isResolved =
        statusLower === "checked out" ||
        statusLower === "completed" ||
        statusLower === "cancelled" ||
        statusLower === "canceled" ||
        statusLower === "rejected";
      if (isResolved) return false;
      if (!a?.start || !dayjs(a.start).isValid()) return false;
      return now.diff(dayjs(a.start), "day") > 1;
    };

    const upcoming30Days = (appointments || []).filter((a) => {
      if (!a.start || !dayjs(a.start).isValid()) return false;
      const start = dayjs(a.start);
      return (
        !start.isBefore(now.startOf("day")) &&
        !start.isAfter(now.add(SCHEDULE_LOOKAHEAD_DAYS, "day").endOf("day"))
      );
    }).length;
    const totalToday = (appointments || []).filter(
      (a) => a.start && dayjs(a.start).isSame(now, "day"),
    ).length;
    const checkedIn = (appointments || []).filter(
      (a) => !isPassedDay(a) && a.status === "Checked In",
    ).length;
    const startingSoon = (appointments || []).filter((a) => {
      if (!a.start) return false;
      const start = dayjs(a.start);
      // within next 8 hours
      return start.isAfter(now) && start.diff(now, "minute") <= 480;
    }).length;
    const checkedOut = (appointments || []).filter(
      (a) => a.status === "Checked Out",
    ).length;
    const delayed = (appointments || []).filter(
      (a) => !isPassedDay(a) && a.status === "Delayed",
    ).length;
    const passedDay = (appointments || []).filter((a) => isPassedDay(a)).length;
    // Server-computed counts cover the full window with no cap; the local
    // derivation only serves as fallback when stats are unavailable.
    if (serverStats) return serverStats;
    return {
      upcoming30Days,
      totalToday,
      checkedIn,
      startingSoon,
      checkedOut,
      delayed,
      passedDay,
    };
  }, [appointments, serverStats]);

  // Lock scroll when any modal is open
  useEffect(() => {
    if (
      isModalOpen ||
      isCheckInModalOpen ||
      isCheckOutModalOpen ||
      isDeleteModalOpen
    ) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isModalOpen, isCheckInModalOpen, isCheckOutModalOpen, isDeleteModalOpen]);

  // Auto-clear delete message after a few seconds
  useEffect(() => {
    if (!deleteMessage) return;
    const t = setTimeout(() => setDeleteMessage(""), 6000);
    return () => clearTimeout(t);
  }, [deleteMessage]);

  // Address autocomplete (debounced, fast) - Only if Maps API available
  useEffect(() => {
    if (addressTimer.current) clearTimeout(addressTimer.current);

    if (!addressQuery || addressQuery.length < 1) {
      setAddressSuggestions([]);
      return;
    }
    addressTimer.current = setTimeout(() => {
      loadAddressSuggestions(addressQuery);
    }, 140);
    return () => {
      if (addressTimer.current) clearTimeout(addressTimer.current);
    };
  }, [addressQuery, loadAddressSuggestions]);

  const retryGeocode = async (appt) => {
    if (!GOOGLE_MAPS_KEY) {
      console.warn("retryGeocode: Google Maps API key not configured");
      setGeocodeWarning("Google Maps API key not configured");
      return;
    }
    if (!appt || !appt.address) {
      console.warn("retryGeocode: No appointment or address provided");
      return;
    }
    try {
      setLoading(true);
      setGeocodeWarning(""); // Clear previous warnings
      console.log("🔍 Geocoding address:", appt.address);
      const results = await fetchAddressSuggestions(appt.address);
      console.log("📍 Geocoding results:", results);
      if (results && results.length > 0) {
        const best = results[0];
        const lat = Number(best.lat);
        const lng = Number(best.lon);
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
          console.log("✅ Geocoding successful:", {
            lat,
            lng,
            formatted: best.display_name,
          });
          await updateAppointment(appt.id || appt._id, {
            coords: { lat, lng },
          });
          // Update local state
          setActiveAppointment((prev) =>
            prev && (prev.id === appt.id || prev._id === appt._id)
              ? { ...prev, coords: { lat, lng } }
              : prev,
          );
          setAppointments((prev) =>
            (prev || []).map((a) =>
              a.id === appt.id || a._id === appt._id
                ? { ...a, coords: { lat, lng } }
                : a,
            ),
          );
          setGeocodeWarning(""); // Clear warning on success
        } else {
          console.error("❌ Invalid coordinates:", best);
          setGeocodeWarning(
            "Could not determine valid coordinates from address.",
          );
        }
      } else {
        console.error("❌ No geocoding results for:", appt.address);
        setGeocodeWarning(
          "Unable to geocode address. This may be due to Google API configuration. Appointment saved without map location.",
        );
      }
    } catch (e) {
      console.error("❌ Geocoding error:", e);
      setGeocodeWarning(
        "Geocoding service unavailable. Appointment saved without map location.",
      );
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditId(null);
    setError("");
    setFieldErrors({});
    setProviderAvailabilityInfo(null);
    setAvailabilityWarning("");
    setVoiceProvisionWarning("");
    setForm({
      client: "",
      clientId: "",
      clientPhone: "",
      provider: "",
      providerId: "",
      providerRole: "Nurse",
      providerAvailability: "available",
      recurrence: "One-time",
      appointmentsDraft: [
        {
          date: dayjs().format("YYYY-MM-DD"),
          startTime: "09:00",
          durationMin: 60,
        },
      ],
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      start: "",
      end: "",
      address: "",
      lat: "",
      lng: "",
      notes: "",
      showCheckInQuestions: true,
      showCheckOutQuestions: true,
      showAgentAlbert: true,
      showVoiceAnalysis: true,
    });
    setCheckInQuestionSetId("");
    setCheckOutQuestionSetId("");
    setIsModalOpen(true);
  };

  const normalizeId = (val) => {
    if (!val) return "";
    if (typeof val === "string") return val;
    if (typeof val === "object") return val.id || val._id || "";
    return "";
  };

  const normalizeQuestionSetId = (val) => {
    if (!val) return "";
    if (typeof val === "string") return val;
    if (typeof val === "object") return val.id || val._id || "";
    return "";
  };

  const openEditModal = (appt) => {
    setEditId(appt.id);
    setError("");
    setFieldErrors({});
    setVoiceProvisionWarning("");

    // Find client to get fallback address if appointment doesn't have one
    const clientId = normalizeId(appt.clientId);
    const client = clientsList.find((c) => (c.id || c._id) === clientId);
    const fallbackAddress = appt.address || buildClientAddress(client);
    const startDate = appt.start ? dayjs(appt.start) : null;
    const endDate = appt.end ? dayjs(appt.end) : null;
    const singleDate = startDate ? startDate.format("YYYY-MM-DD") : "";
    const startTime = startDate ? startDate.format("HH:mm") : "";
    const durationMin =
      startDate && endDate ? endDate.diff(startDate, "minute") || 60 : 60;
    const appointmentsDraft = [
      {
        date: singleDate || dayjs().format("YYYY-MM-DD"),
        startTime: startTime || "09:00",
        durationMin,
      },
    ];

    setForm({
      client: appt.client || appt.clientName || "",
      clientId: clientId,
      clientPhone: appt.clientPhone || "",
      provider: appt.provider || appt.providerName || "",
      providerId: normalizeId(appt.providerId),
      providerRole: appt.providerRole || "Nurse",
      providerAvailability: appt.providerAvailability || "available",
      recurrence: appt.recurrence || "One-time",
      // Draft rows (edit mode locked to single row)
      appointmentsDraft,
      timezone:
        appt.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      // Legacy fields kept for update payload compatibility
      start: appt.start ? startDate.format("YYYY-MM-DDTHH:mm") : "",
      end: appt.end ? endDate.format("YYYY-MM-DDTHH:mm") : "",
      address: fallbackAddress,
      lat: appt.coords?.lat || "",
      lng: appt.coords?.lng || "",
      notes: appt.notes || "",
      showCheckInQuestions:
        appt.showCheckInQuestions === undefined
          ? true
          : !!appt.showCheckInQuestions,
      showCheckOutQuestions:
        appt.showCheckOutQuestions === undefined
          ? true
          : !!appt.showCheckOutQuestions,
      showAgentAlbert:
        appt.showAgentAlbert === undefined ? true : !!appt.showAgentAlbert,
      showVoiceAnalysis:
        appt.showVoiceAnalysis === undefined ? true : !!appt.showVoiceAnalysis,
    });
    setCheckInQuestionSetId(
      normalizeQuestionSetId(
        appt.checkInQuestionSetId || appt.checkInQuestionSet,
      ),
    );
    setCheckOutQuestionSetId(
      normalizeQuestionSetId(
        appt.checkOutQuestionSetId || appt.checkOutQuestionSet,
      ),
    );
    const provider = providersList.find(
      (p) => (p.id || p._id) === normalizeId(appt.providerId),
    );
    setProviderAvailabilityInfo(
      parseAvailabilityString(provider?.availability),
    );
    if (fallbackAddress) {
      setAddressQuery(fallbackAddress);
    }
    setIsModalOpen(true);
  };

  const addDraftRow = () => {
    setForm((prev) => ({
      ...prev,
      appointmentsDraft: [
        ...(prev.appointmentsDraft || []),
        {
          date: dayjs().format("YYYY-MM-DD"),
          startTime: "09:00",
          durationMin: 60,
        },
      ],
    }));
  };

  const removeDraftRow = (idx) => {
    setForm((prev) => ({
      ...prev,
      appointmentsDraft: (prev.appointmentsDraft || []).filter(
        (_, i) => i !== idx,
      ),
    }));
  };

  const updateDraftRow = (idx, changes) => {
    setForm((prev) => {
      const nextRows = [...(prev.appointmentsDraft || [])];
      nextRows[idx] = { ...nextRows[idx], ...changes };
      return { ...prev, appointmentsDraft: nextRows };
    });
  };

  // Live availability warning when provider availability and draft rows change
  useEffect(() => {
    if (!providerAvailabilityInfo || providerAvailabilityInfo.length === 0) {
      setAvailabilityWarning("");
      return;
    }
    const violates = checkAvailabilityConflict(
      form.appointmentsDraft,
      providerAvailabilityInfo,
    );
    setAvailabilityWarning(
      violates
        ? "Selected dates/times are outside caregiver availability."
        : "",
    );
  }, [providerAvailabilityInfo, form.appointmentsDraft]);

  const checkAvailabilityConflict = (draftRows, slots) => {
    if (!slots || slots.length === 0) return false;
    const dayName = (dateStr) => dayjs(dateStr).format("ddd"); // Mon, Tue...
    return (draftRows || []).some((row) => {
      const dName = dayName(row.date);
      const slot = slots.find((s) => s.day === dName);
      if (!slot) return true;
      const start = row.startTime;
      const dur = Number(row.durationMin || 0);
      const endTime =
        start && dur
          ? dayjs(`2000-01-01 ${start}`).add(dur, "minute").format("HH:mm")
          : "";
      const withinTime =
        start &&
        slot.start &&
        slot.end &&
        start >= slot.start &&
        endTime <= slot.end;
      return !withinTime;
    });
  };

  const buildRecurrenceDrafts = (baseRow, recurrenceValue) => {
    const drafts = [];
    const baseDate = baseRow?.date
      ? dayjs(baseRow.date)
      : dayjs().startOf("day");
    for (let i = 0; i < RECURRENCE_OCCURRENCES; i++) {
      let date = baseDate;
      if (recurrenceValue === "Weekly") {
        date = baseDate.add(i, "week");
      } else if (recurrenceValue === "Bi-weekly") {
        date = baseDate.add(i * 2, "week");
      } else if (recurrenceValue === "Monthly") {
        date = baseDate.add(i, "month");
      } else if (recurrenceValue === "Quarterly") {
        date = baseDate.add(i * 3, "month");
      }
      drafts.push({
        date: date.format("YYYY-MM-DD"),
        startTime: baseRow.startTime || "09:00",
        durationMin: baseRow.durationMin || 60,
      });
    }
    return drafts;
  };

  const handleRecurrenceChange = (recurrenceValue) => {
    const baseRow = form.appointmentsDraft?.[0] || {
      date: dayjs().format("YYYY-MM-DD"),
      startTime: "09:00",
      durationMin: 60,
    };
    let nextDrafts = [baseRow];
    if (
      recurrenceValue &&
      recurrenceValue !== "One-time" &&
      recurrenceValue !== "All"
    ) {
      nextDrafts = buildRecurrenceDrafts(baseRow, recurrenceValue);
    }
    setForm((prev) => ({
      ...prev,
      recurrence: recurrenceValue,
      appointmentsDraft: nextDrafts,
    }));
  };

  const handleSave = async () => {
    const digitsOnly = (form.clientPhone || "").replace(/\D/g, "");
    const trimmedAddress = (form.address || "").trim();
    const validationErrors = {};

    if (!form.clientId) {
      validationErrors.clientId = "Client is required.";
    }
    if (!digitsOnly || digitsOnly.length !== 10) {
      validationErrors.clientPhone = "Client phone must be 10 digits.";
    }
    if (!form.providerRole) {
      validationErrors.providerRole = "Provider role is required.";
    }
    if (!form.providerId && !form.provider) {
      validationErrors.provider = "Please select a provider.";
    }
    if (!form.appointmentsDraft || form.appointmentsDraft.length === 0) {
      validationErrors.dates = "Add at least one appointment.";
    }
    if (form.appointmentsDraft && form.appointmentsDraft.length > 0) {
      form.appointmentsDraft.forEach((row, idx) => {
        const dur = Number(row.durationMin);
        if (!row.date || !row.startTime || !row.durationMin) {
          validationErrors.dates = `Missing data in row ${idx + 1}`;
        } else if (dur < 60 || dur % 30 !== 0) {
          validationErrors.dates = `Duration must be ≥ 60 and in 30-min steps (row ${
            idx + 1
          })`;
        }
      });
    }
    if (!form.recurrence) {
      validationErrors.recurrence = "Recurrence is required.";
    }

    const violatesAvailability = checkAvailabilityConflict(
      form.appointmentsDraft,
      providerAvailabilityInfo,
    );

    if (violatesAvailability) {
      validationErrors.availability =
        "Selected dates/times are outside caregiver availability.";
    }
    if (!trimmedAddress) {
      validationErrors.address = "Address is required.";
    }
    if ((form.showCheckInQuestions ?? true) && !checkInQuestionSetId) {
      validationErrors.checkInQuestionSetId =
        "Select a check-in set or turn off Check-in Questions.";
    }
    if ((form.showCheckOutQuestions ?? true) && !checkOutQuestionSetId) {
      validationErrors.checkOutQuestionSetId =
        "Select a check-out set or turn off Check-out Questions.";
    }
    if (!activeLocation?.id && !activeLocation?._id) {
      validationErrors.location =
        "Select a location before creating an appointment.";
    }

    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      setError(Object.values(validationErrors)[0]);
      return;
    }

    setFieldErrors({});
    // Only send clientId if it looks like a valid Mongo ObjectId (24 hex chars)
    const validClientId =
      form.clientId && /^[a-fA-F0-9]{24}$/.test(form.clientId.trim())
        ? form.clientId.trim()
        : undefined;
    try {
      setLoading(true);
      setError("");
      setVoiceProvisionWarning("");
      const isAlbert =
        form.provider === "AlbertAi" ||
        (form.providerId || "").startsWith("albert-ai");

      // Best-effort: ensure Canary subject exists when Voice Analysis is enabled.
      // Non-blocking: if Canary is disabled/misconfigured, we still allow scheduling.
      const wantsVoice = form.showVoiceAnalysis === undefined ? true : !!form.showVoiceAnalysis;
      if (wantsVoice && validClientId) {
        try {
          await getCanarySubject(validClientId);
        } catch (err) {
          const msg =
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.message ||
            "Voice Analysis is currently unavailable. Please contact support.";
          setVoiceProvisionWarning(msg);
        }
      }

      const basePayload = {
        clientName: form.client,
        clientId: validClientId,
        clientPhone: digitsOnly,
        providerId: isAlbert ? undefined : form.providerId || undefined,
        providerName: isAlbert ? "AlbertAi" : form.provider, // required by backend
        providerRole: form.providerRole,
        status: form.status || "Pending",
        recurrence: form.recurrence,
        checkInQuestionSetId: checkInQuestionSetId || undefined,
        checkOutQuestionSetId: checkOutQuestionSetId || undefined,
        showCheckInQuestions:
          form.showCheckInQuestions === undefined
            ? true
            : !!form.showCheckInQuestions,
        showCheckOutQuestions:
          form.showCheckOutQuestions === undefined
            ? true
            : !!form.showCheckOutQuestions,
        showAgentAlbert:
          form.showAgentAlbert === undefined ? true : !!form.showAgentAlbert,
        showVoiceAnalysis:
          form.showVoiceAnalysis === undefined
            ? true
            : !!form.showVoiceAnalysis,
        address: form.address,
        coords:
          form.lat && form.lng
            ? { lat: Number(form.lat), lng: Number(form.lng) }
            : undefined,
        notes: form.notes,
        locationId: activeLocation?.id || activeLocation?._id,
        timezone: form.timezone,
      };
      console.log("💾 Saving appointment with address:", form.address);
      if (editId) {
        const firstRow = form.appointmentsDraft?.[0];
        const singleDate = firstRow?.date;
        const sched = firstRow;
        const editStart =
          singleDate && sched?.startTime
            ? combineDateTime(singleDate, sched.startTime, form.timezone)
            : form.start
              ? new Date(form.start)
              : null;
        const editEnd =
          editStart && sched?.durationMin
            ? dayjs(editStart).add(sched.durationMin, "minute").toDate()
            : form.end
              ? new Date(form.end)
              : null;
        await updateAppointment(editId, {
          ...basePayload,
          start: editStart,
          end: editEnd,
        });
      } else {
        const appointmentsPayload = (form.appointmentsDraft || []).map(
          (row) => {
            const start = combineDateTime(
              row.date,
              row.startTime,
              form.timezone,
            );
            const end = start
              ? dayjs(start)
                  .add(row.durationMin || 60, "minute")
                  .toDate()
              : null;
            return {
              ...basePayload,
              start,
              end,
            };
          },
        );

        for (const appt of appointmentsPayload) {
          await createAppointment(appt);
        }
      }
      setIsModalOpen(false);
      // Refresh list
      const filters = {
        status: statusFilter,
        providerRole: providerFilter,
        recurrence: recurrenceFilter,
        q: search || undefined,
        locationId: activeLocation?.id || activeLocation?._id,
      };
      const payload = await fetchAppointments({
        ...filters,
        includeStats: true,
      });
      const data = Array.isArray(payload)
        ? payload
        : payload?.appointments || [];
      setServerStats(Array.isArray(payload) ? null : payload?.stats || null);
      setAppointments(data);
      if (data && data.length > 0) setActiveAppointment(data[0]);
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to save appointment",
      );
    } finally {
      setLoading(false);
    }
  };

  const isFormValid =
    !!form.clientId &&
    !!form.clientPhone &&
    form.clientPhone.replace(/\D/g, "").length === 10 &&
    !!form.providerRole &&
    !!form.providerId &&
    (form.appointmentsDraft || []).length > 0 &&
    !(form.appointmentsDraft || []).some((r) => {
      const dur = Number(r.durationMin);
      return (
        !r.date || !r.startTime || !r.durationMin || dur < 60 || dur % 30 !== 0
      );
    }) &&
    !!form.recurrence &&
    !!(form.address || "").trim() &&
    (activeLocation?.id || activeLocation?._id);

  console.log("🔄 Appointments render:", {
    clientsListLength: clientsList.length,
    formClientId: form.clientId,
    formAddress: form.address,
    isModalOpen: isModalOpen,
  });

  // Check if current appointment has valid location
  const hasValidLocation = !!(
    activeAppointment?.coords?.lat && activeAppointment?.coords?.lng
  );

  const clientOptions = useMemo(
    () =>
      clientsList.map((c) => ({
        value: c.id || c._id,
        label: `${c.firstName || ""} ${c.lastName || ""}`.trim() || "Unnamed",
      })),
    [clientsList],
  );

  const providerOptions = useMemo(
    () =>
      filteredProviders.map((p) => ({
        value: p.id || p._id || p.name,
        label: p.name,
      })),
    [filteredProviders],
  );

  const checkInOptions = useMemo(
    () =>
      checkInSets.map((s) => ({
        value: s.id || s._id,
        label: s.title,
      })),
    [checkInSets],
  );

  const checkOutOptions = useMemo(
    () =>
      checkOutSets.map((s) => ({
        value: s.id || s._id,
        label: s.title,
      })),
    [checkOutSets],
  );

  return (
    <div className="appointments-container page-shell">
      <PageHeader
        title="Client Schedule"
        subtitle="Create, assign, and track appointments in real time."
        icon={<Calendar className="h-6 w-6 text-white" />}
        activeLocation={activeLocation}
        locations={locations}
        onChangeLocation={onChangeLocation}
        companyDisplayName={companyDisplayName}
        currentUser={currentUser}
        onLogout={onLogout}
        onLock={onLock}
      />
      {geocodeWarning && !hasValidLocation && (
        <div
          style={{
            background: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "8px",
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontSize: "20px" }}>⚠️</span>
            <div>
              <div style={{ fontWeight: 600, color: "#856404" }}>
                Geocoding Warning
              </div>
              <div style={{ fontSize: "14px", color: "#856404" }}>
                {geocodeWarning}
              </div>
            </div>
          </div>
          <button
            onClick={() => setGeocodeWarning("")}
            style={{
              background: "none",
              border: "none",
              fontSize: "20px",
              cursor: "pointer",
              color: "#856404",
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Detail Modal Removed */}

      <div className="appointments-stats-grid">
        <div
          className={`stat-card ${statFilter === "upcoming30" ? "active" : ""}`}
          onClick={() => setStatFilter("upcoming30")}
          title={`Schedules from today through the next ${SCHEDULE_LOOKAHEAD_DAYS} days`}
        >
          <div className="stat-title">Next 30 Days</div>
          <div className="stat-value">{derivedStats.upcoming30Days || 0}</div>
          <div className="stat-foot">Today + upcoming</div>
        </div>
        <div
          className={`stat-card ${statFilter === "today" ? "active" : ""}`}
          onClick={() => setStatFilter("today")}
        >
          <div className="stat-title">Total Today</div>
          <div className="stat-value">{derivedStats.totalToday || 0}</div>
          <div className="stat-foot">
            <span>In progress</span>
            <FontAwesomeIcon icon={faClock} />
          </div>
        </div>
        <div
          className={`stat-card ${statFilter === "soon" ? "active" : ""}`}
          onClick={() => setStatFilter("soon")}
        >
          <div className="stat-title">Starting Soon</div>
          <div className="stat-value">{derivedStats.startingSoon || 0}</div>
          <div className="stat-foot">Within 8 hours</div>
        </div>
        <div
          className={`stat-card ${statFilter === "checkedin" ? "active" : ""}`}
          onClick={() => setStatFilter("checkedin")}
        >
          {" "}
          <div className="stat-title">Checked In</div>
          <div className="stat-value">{derivedStats.checkedIn || 0}</div>
          <div className="stat-foot">Live updates</div>
        </div>
        <div
          className={`stat-card ${statFilter === "checkedout" ? "active" : ""}`}
          onClick={() => setStatFilter("checkedout")}
        >
          <div className="stat-title">Checked Out</div>
          <div className="stat-value">{derivedStats.checkedOut || 0}</div>
          <div className="stat-foot">Pending review</div>
        </div>
        <div
          className={`stat-card ${statFilter === "delayed" ? "active" : ""}`}
          onClick={() => setStatFilter("delayed")}
        >
          {" "}
          <div className="stat-title">Delayed</div>
          <div className="stat-value">{derivedStats.delayed || 0}</div>
          <div className="stat-foot">After check-in window</div>
        </div>
        <div
          className={`stat-card ${statFilter === "passed" ? "active" : ""}`}
          onClick={() => setStatFilter("passed")}
          title="Schedules older than 1 day (manual management)"
        >
          <div className="stat-title">Passed Day</div>
          <div className="stat-value">{derivedStats.passedDay || 0}</div>
          <div className="stat-foot">Manual manage</div>
        </div>
      </div>

      <div className="filters-bar">
        <div className="filters-left">
          <div className="filter-group">
            <FontAwesomeIcon icon={faFilter} className="filter-icon" />
            <input
              type="text"
              placeholder="Search by client or provider..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="filter-select">
            <span>Status</span>
            <div className="select-box">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {statusChips.map((s) => (
                  <option key={s.value} value={s.value}>
                    {`${s.icon} ${s.label}`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="filter-select">
            <span>Provider Type</span>
            <div className="select-box">
              <select
                value={providerFilter}
                onChange={(e) => setProviderFilter(e.target.value)}
              >
                {providerTypes.map((s) => (
                  <option key={s.value} value={s.value}>
                    {`${s.icon} ${s.label}`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="filter-select">
            <span>Recurrence</span>
            <div className="select-box">
              <select
                value={recurrenceFilter}
                onChange={(e) => setRecurrenceFilter(e.target.value)}
              >
                {recurrences.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="page-actions">
          <button className="primary-btn" onClick={openCreateModal}>
            <FontAwesomeIcon icon={faCalendarPlus} /> New Client Schedule
          </button>
        </div>
      </div>

      <div className="appointments-split-view hide-map">
        {/* LEFT: MAP PANE */}
        <div className="map-pane">
          {!GOOGLE_MAPS_KEY ? (
            <div className="map-placeholder-large">
              <div
                style={{
                  fontSize: "48px",
                  color: "#cbd5e1",
                  marginBottom: "16px",
                }}
              >
                🗺️
              </div>
              <div
                style={{
                  fontWeight: "600",
                  color: "#64748b",
                  marginBottom: "8px",
                }}
              >
                Map Feature Disabled
              </div>
              <div style={{ fontSize: "14px", color: "#94a3b8" }}>
                Google Maps API key not configured.
                <br />
                Client schedules can still be created and managed normally.
              </div>
            </div>
          ) : activeAppointment?.coords?.lat &&
            activeAppointment?.coords?.lng ? (
            <div className="map-container-wrapper">
              {loadError && (
                <div
                  style={{ padding: "20px", color: "red", textAlign: "center" }}
                >
                  Map cannot be loaded. Please check API key configuration and
                  billing.
                  <br />
                  <small>{loadError.message}</small>
                </div>
              )}
              {isLoaded ? (
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={{
                    lat: Number(activeAppointment.coords.lat),
                    lng: Number(activeAppointment.coords.lng),
                  }}
                  zoom={15}
                  options={{
                    disableDefaultUI: false,
                    zoomControl: true,
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: false,
                  }}
                  onLoad={(map) => {
                    mapInstanceRef.current = map;
                    console.log("✅ Google Map Loaded", {
                      mapReady: !!mapInstanceRef.current,
                      googleMapsReady: !!window.google,
                    });
                    // If there's already an active appointment with coords, show it
                    if (
                      activeAppointment?.coords?.lat &&
                      activeAppointment?.coords?.lng
                    ) {
                      console.log(
                        "🎯 Map loaded with active appointment, showing marker",
                      );
                      setTimeout(
                        () => showLocationOnMap(activeAppointment),
                        100,
                      );
                    }
                  }}
                >
                  {/* Markers are created imperatively via showLocationOnMap() */}
                </GoogleMap>
              ) : (
                !loadError && (
                  <div
                    style={{
                      padding: "20px",
                      textAlign: "center",
                      color: "#64748b",
                    }}
                  >
                    Loading Map...
                  </div>
                )
              )}
              <div className="map-coords">
                {`${Number(activeAppointment.coords.lat).toFixed(4)}, ${Number(
                  activeAppointment.coords.lng,
                ).toFixed(4)}`}
              </div>
            </div>
          ) : (
            <div className="map-placeholder-large">
              <div
                style={{
                  fontSize: "48px",
                  color: "#cbd5e1",
                  marginBottom: "16px",
                }}
              >
                🗺️
              </div>
              <div style={{ fontWeight: "600", color: "#64748b" }}>
                {activeAppointment
                  ? "Location pending for this appointment"
                  : "Select an appointment to view on map"}
              </div>
              {GOOGLE_MAPS_KEY && activeAppointment?.address && (
                <button
                  className="ghost-btn small"
                  onClick={() => retryGeocode(activeAppointment)}
                >
                  <FontAwesomeIcon icon={faRedoAlt} /> Retry Location
                </button>
              )}
            </div>
          )}
        </div>

        {/* RIGHT: LIST PANE */}
        <div className="list-pane">
          {loading && <div className="table-row">Loading...</div>}
          {error && (
            <div style={{ color: "red", padding: "10px" }}>{error}</div>
          )}
          <div className="appt-table-head">
            <div className="appt-col">
              <div className="col-title">
                <FontAwesomeIcon icon={faUserFriends} className="col-icon" />
                Client
              </div>
            </div>
            <div className="appt-col">
              <div className="col-title">
                <FontAwesomeIcon icon={faUserNurse} className="col-icon" />
                Caregiver / Nurse
              </div>
            </div>
            <div className="appt-col">
              <div className="col-title">
                <FontAwesomeIcon icon={faClipboardCheck} className="col-icon" />
                Status
              </div>
            </div>
            <div className="appt-col">
              <div className="col-title">
                <FontAwesomeIcon icon={faClock} className="col-icon" />
                Time
              </div>
            </div>
            <div className="appt-col actions-col">
              <div className="col-title" aria-hidden="true">
                {/* empty for actions */}
              </div>
            </div>
          </div>

          {pagedAppointments.map((appt) => {
            const isActive = activeAppointment?.id === appt.id;
            const providerName = getProviderName(appt);
            const statusLabel = getDerivedStatus(appt);
            const statusColor = getStatusColor(appt);
            const timeLabel = appt.start
              ? `${dayjs(appt.start).format("MMM D, HH:mm")} - ${
                  appt.end ? dayjs(appt.end).format("HH:mm") : "--"
                }`
              : "--";
            const recurrenceLabel = appt.recurrence || "One-time";
            return (
              <div
                key={appt.id}
                className={`appt-table-row ${isActive ? "active" : ""}`}
                onClick={() => {
                  console.log("📍 Appointment clicked:", {
                    id: appt.id,
                    client: appt.clientName,
                    hasCoords: !!(appt.coords?.lat && appt.coords?.lng),
                    coords: appt.coords,
                  });
                  setActiveAppointment(appt);
                  setIsDetailOpen(true);
                }}
              >
                <div className="appt-col client-col">
                  <div className="client-avatar">
                    <FontAwesomeIcon icon={faUserFriends} />
                  </div>
                  <div className="client-name">
                    {appt.clientName ||
                      appt.client?.name ||
                      (appt.client
                        ? `${appt.client.firstName || ""} ${
                            appt.client.lastName || ""
                          }`.trim()
                        : "Unnamed")}
                  </div>
                </div>
                <div className="appt-col provider-col">{providerName}</div>
                <div className="appt-col status-col">
                  <span
                    className={`status-pill status-${statusLabel
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`}
                    style={{ color: statusColor }}
                  >
                    {statusLabel}
                  </span>
                  {appt.acceptedLate && (
                    <span className="late-flag">Accepted late</span>
                  )}
                  {isLateCheckIn(appt) && (
                    <span className="late-flag">Late check-in</span>
                  )}
                </div>
                <div className="appt-col time-col">
                  {timeLabel} • {recurrenceLabel}
                </div>
                <div className="appt-col actions-col">
                  <button
                    className="action-icon-btn danger"
                    title="Delete appointment"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(appt);
                      setIsDeleteModalOpen(true);
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            );
          })}

          {filteredAppointments.length === 0 && (
            <div className="table-row empty">No appointments found</div>
          )}

          {filteredAppointments.length > 0 && (
            <div className="pagination-bar">
              <div className="pagination-meta">
                Showing {(currentPage - 1) * pageSize + 1}-
                {Math.min(currentPage * pageSize, filteredAppointments.length)}{" "}
                of {filteredAppointments.length}
              </div>
              <div className="pagination-controls">
                <button
                  className="ghost-btn small"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </button>
                <div className="page-indicator">
                  Page {currentPage} / {totalPages}
                </div>
                <button
                  className="ghost-btn small"
                  disabled={currentPage === totalPages}
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {isDetailOpen && activeAppointment && (
        <div
          className="appt-modal-overlay appt-detail-overlay"
          onClick={() => {
            setIsDetailOpen(false);
            setSelectedMarker(null);
          }}
        >
          <div
            className="appt-detail-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="detail-header">
              <div>
                <div className="detail-title">
                  {activeAppointment.clientName || "Client"}
                </div>
                <div className="detail-sub">
                  {activeAppointment.start
                    ? dayjs(activeAppointment.start).format("MMM D, h:mm A")
                    : "N/A"}{" "}
                  •{" "}
                  {activeAppointment.end
                    ? dayjs(activeAppointment.end).format("h:mm A")
                    : "N/A"}
                </div>
              </div>
              <div className="detail-actions">
                <button
                  className="ghost-btn"
                  onClick={() => {
                    setIsDetailOpen(false);
                    setSelectedMarker(null);
                  }}
                >
                  Close
                </button>
              </div>
            </div>

            <div className="detail-body">
              <div className="detail-info">
                <div className="meta-item">
                  <FontAwesomeIcon icon={faUserFriends} />
                  <span>{getProviderName(activeAppointment)}</span>
                </div>
                <div className="meta-item">
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                  <span>
                    {activeAppointment.address ||
                      activeAppointment.clientAddress ||
                      buildClientAddress(activeAppointment.client) ||
                      "Address not set"}
                  </span>
                </div>
                <div className="meta-item">
                  <span
                    className="status-pill"
                    style={{ color: getStatusColor(activeAppointment) }}
                  >
                    {getDerivedStatus(activeAppointment) || "Pending"}
                  </span>
                </div>
                {activeAppointment.acceptedLate && (
                  <div className="meta-item">
                    <span className="late-flag">Accepted late</span>
                  </div>
                )}
                {isLateCheckIn(activeAppointment) && (
                  <div className="meta-item">
                    <span className="late-flag">Late check-in</span>
                  </div>
                )}
                {(activeAppointment.checkInTime ||
                  activeAppointment.checkOutTime ||
                  activeAppointment.acceptedAt ||
                  activeAppointment.declinedAt) && (
                  <>
                    {activeAppointment.checkInTime && (
                      <div className="meta-item">
                        <FontAwesomeIcon icon={faClock} />
                        <span>
                          Check-in:{" "}
                          {dayjs(activeAppointment.checkInTime).format(
                            "MMM D, h:mm A",
                          )}
                        </span>
                      </div>
                    )}
                    {activeAppointment.checkOutTime && (
                      <div className="meta-item">
                        <FontAwesomeIcon icon={faClock} />
                        <span>
                          Check-out:{" "}
                          {dayjs(activeAppointment.checkOutTime).format(
                            "MMM D, h:mm A",
                          )}
                        </span>
                      </div>
                    )}
                  </>
                )}
                {(activeAppointment.acceptedAt ||
                  activeAppointment.declinedAt) && (
                  <div className="meta-item">
                    <FontAwesomeIcon icon={faClock} />
                    <span>
                      {activeAppointment.acceptedAt
                        ? `Accepted: ${dayjs(
                            activeAppointment.acceptedAt,
                          ).format("MMM D, h:mm A")}`
                        : `Declined: ${dayjs(
                            activeAppointment.declinedAt,
                          ).format("MMM D, h:mm A")}`}
                    </span>
                  </div>
                )}
                {activeAppointment.notes && (
                  <div className="detail-notes">
                    “{activeAppointment.notes}”
                  </div>
                )}
                <div className="detail-buttons">
                  <button
                    className="action-btn btn-secondary"
                    onClick={() => {
                      setIsDetailOpen(false);
                      openEditModal(activeAppointment);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className="action-btn btn-check-in"
                    onClick={() => {
                      setIsDetailOpen(false);
                      setIsCheckInModalOpen(true);
                    }}
                  >
                    Check-in
                  </button>
                  <button
                    className="action-btn btn-check-out"
                    onClick={() => {
                      setIsDetailOpen(false);
                      setIsCheckOutModalOpen(true);
                    }}
                  >
                    Check-out
                  </button>
                </div>
              </div>

              <div className="detail-map">
                {!GOOGLE_MAPS_KEY ? (
                  <div className="map-placeholder-large">
                    <div className="map-emoji">🗺️</div>
                    <div className="map-title">Map Feature Disabled</div>
                    <div className="map-text">
                      Google Maps API key not configured.
                    </div>
                  </div>
                ) : activeAppointment?.coords?.lat &&
                  activeAppointment?.coords?.lng ? (
                  <div className="map-container-wrapper">
                    {loadError && (
                      <div className="map-error">
                        Map cannot be loaded. Please check API key configuration
                        and billing.
                        <br />
                        <small>{loadError.message}</small>
                      </div>
                    )}
                    {isLoaded ? (
                      <GoogleMap
                        mapContainerStyle={mapContainerStyle}
                        center={{
                          lat: Number(activeAppointment.coords.lat),
                          lng: Number(activeAppointment.coords.lng),
                        }}
                        zoom={15}
                        options={{
                          disableDefaultUI: false,
                          zoomControl: true,
                          streetViewControl: false,
                          mapTypeControl: false,
                          fullscreenControl: false,
                        }}
                        onLoad={(map) => {
                          mapInstanceRef.current = map;
                          if (
                            activeAppointment?.coords?.lat &&
                            activeAppointment?.coords?.lng
                          ) {
                            setTimeout(
                              () => showLocationOnMap(activeAppointment),
                              100,
                            );
                          }
                        }}
                      >
                        {/* markers imperatively handled */}
                      </GoogleMap>
                    ) : (
                      !loadError && (
                        <div className="map-loading">Loading Map...</div>
                      )
                    )}
                    <div className="map-coords">
                      {`${Number(activeAppointment.coords.lat).toFixed(
                        4,
                      )}, ${Number(activeAppointment.coords.lng).toFixed(4)}`}
                    </div>
                  </div>
                ) : (
                  <div className="map-placeholder-large">
                    <div className="map-emoji">📍</div>
                    <div className="map-title">
                      No coordinates available for this appointment
                    </div>
                    <div className="map-text">
                      Add an address to see it on the map.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {isModalOpen && (
        <div
          className="appt-modal-overlay"
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="appt-modal appointment-form-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-head">
              <h3>{editId ? "Edit Client Schedule" : "New Client Schedule"}</h3>
            </div>
            {fieldErrors.location && (
              <div className="modal-error">{fieldErrors.location}</div>
            )}

            <div className="modal-content-scroll">
              <div className="modal-form-container">
                {/* --- CLIENT & PROVIDER SECTION --- */}
                <div className="form-section-card">
                  <div className="form-section-header">
                    <h4 className="form-section-title">
                      <FontAwesomeIcon
                        icon={faUserFriends}
                        className="text-indigo-500"
                      />
                      Client &amp; Provider
                    </h4>
                  </div>
                  <div className="form-grid-2-col">
                    <div className="form-group">
                      <label className="form-label">
                        Client <span className="req-star">*</span>
                      </label>
                      <SearchableSelect
                        options={clientOptions}
                        value={form.clientId}
                        placeholder="Select client"
                        onChange={(cid) => {
                          handleClientSelect(cid);
                        }}
                      />
                      {fieldErrors.clientId && (
                        <div className="error-msg">{fieldErrors.clientId}</div>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Client Phone <span className="req-star">*</span>
                      </label>
                      <input
                        type="tel"
                        className="form-input"
                        value={form.clientPhone}
                        maxLength={12}
                        onChange={(e) => {
                          const digits = (e.target.value || "")
                            .replace(/\D/g, "")
                            .slice(0, 10);
                          let formatted = digits;
                          if (digits.length > 6) {
                            formatted = `${digits.slice(0, 3)}-${digits.slice(
                              3,
                              6,
                            )}-${digits.slice(6)}`;
                          } else if (digits.length > 3) {
                            formatted = `${digits.slice(0, 3)}-${digits.slice(
                              3,
                            )}`;
                          }
                          handleFormChange("clientPhone", formatted);
                        }}
                        placeholder="123-456-7890"
                      />
                      {fieldErrors.clientPhone && (
                        <div className="error-msg">
                          {fieldErrors.clientPhone}
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Provider Role <span className="req-star">*</span>
                      </label>
                      <select
                        className="form-select"
                        value={form.providerRole}
                        onChange={(e) => {
                          const role = e.target.value;
                          handleFormChange("providerRole", role);
                          handleFormChange("providerId", "");
                          handleFormChange("provider", "");
                          setProviderAvailabilityInfo(null);
                        }}
                      >
                        <option>Nurse</option>
                        <option>Caregiver</option>
                      </select>
                      {fieldErrors.providerRole && (
                        <div className="error-msg">
                          {fieldErrors.providerRole}
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Select Provider <span className="req-star">*</span>
                      </label>
                      <SearchableSelect
                        options={providerOptions}
                        value={form.providerId}
                        placeholder={
                          filteredProviders.length === 0
                            ? "No providers available"
                            : "Select provider"
                        }
                        onChange={(pid) => {
                          setError("");
                          const found = filteredProviders.find(
                            (p) => (p.id || p._id) === pid,
                          );
                          const parsedAvail = parseAvailabilityString(
                            found?.availability,
                          );
                          setProviderAvailabilityInfo(parsedAvail);
                          if (pid === "albert-ai") {
                            handleFormChange("providerId", "albert-ai");
                            handleFormChange("provider", "AlbertAi");
                          } else if (!pid) {
                            setProviderAvailabilityInfo(null);
                            handleFormChange("providerId", "");
                            handleFormChange("provider", "");
                          } else {
                            handleFormChange("providerId", pid);
                            handleFormChange(
                              "provider",
                              found?.name || form.provider,
                            );
                          }
                        }}
                      />
                      {filteredProviders.length === 0 && form.providerRole && (
                        <div className="error-msg" style={{ marginTop: "4px" }}>
                          No providers found for role "{form.providerRole}"
                        </div>
                      )}
                      {fieldErrors.provider && (
                        <div className="error-msg">{fieldErrors.provider}</div>
                      )}
                      {filteredProviders.length === 0 &&
                        !fieldErrors.provider && (
                          <div
                            style={{
                              marginTop: "8px",
                              fontSize: "13px",
                              color: "#94a3b8",
                            }}
                          >
                            ℹ️ No caregivers/nurses found for this company. Add
                            them in the Caregivers & Nurses page.
                          </div>
                        )}
                    </div>
                  </div>
                </div>

                {/* --- SCHEDULE SECTION --- */}
                <div className="form-section-card">
                  <div className="form-section-header">
                    <h4 className="form-section-title">
                      <FontAwesomeIcon
                        icon={faClock}
                        className="text-indigo-500"
                      />
                      Schedule &amp; Logistics
                    </h4>
                  </div>
                  <div className="form-grid-2-col">
                    {editId && (
                      <div className="info-note span-full">
                        Editing a single appointment only
                      </div>
                    )}
                    {providerAvailabilityInfo &&
                      providerAvailabilityInfo.length > 0 && (
                        <div className="info-note span-full">
                          Availability:{" "}
                          {providerAvailabilityInfo
                            .map((s) => `${s.day} ${s.start}-${s.end}`)
                            .join(", ")}
                        </div>
                      )}
                    {(availabilityWarning || fieldErrors.availability) && (
                      <div className="error-msg span-full">
                        {availabilityWarning || fieldErrors.availability}
                      </div>
                    )}

                    {/* Dates */}
                    <div className="form-group span-full">
                      <label className="form-label">
                        Dates <span className="req-star">*</span>
                      </label>
                      {fieldErrors.dates && (
                        <div className="error-msg">{fieldErrors.dates}</div>
                      )}
                    </div>

                    {/* Per-date schedule table */}
                    <div className="form-group span-full">
                      <div className="date-schedule-table">
                        <div className="date-schedule-head appointment-row">
                          <div>Date</div>
                          <div>Start</div>
                          <div>Duration</div>
                          <div>End</div>
                          <div></div>
                        </div>

                        {(form.appointmentsDraft || []).map((row, idx) => {
                          const startTime = row.startTime || "";
                          const durationMin = row.durationMin || 60;
                          const endTime = startTime
                            ? dayjs(`2000-01-01 ${startTime}`)
                                .add(durationMin, "minute")
                                .format("HH:mm")
                            : "";
                          const endTimeDisplay = endTime
                            ? dayjs(`2000-01-01 ${endTime}`).format("h:mm A")
                            : "";

                          return (
                            <div key={idx} className="appointment-row">
                              <input
                                type="date"
                                className="form-input"
                                value={row.date || ""}
                                onChange={(e) =>
                                  updateDraftRow(idx, {
                                    date: e.target.value,
                                  })
                                }
                              />

                              <TimeInputWithAmPm
                                className="time-input-ampm-field"
                                value={startTime}
                                onChange={(val) =>
                                  updateDraftRow(idx, {
                                    startTime: val,
                                  })
                                }
                              />

                              <input
                                type="number"
                                min={60}
                                step={30}
                                className="form-input"
                                value={durationMin}
                                onChange={(e) =>
                                  updateDraftRow(idx, {
                                    durationMin: Number(e.target.value),
                                  })
                                }
                              />

                              <input
                                className="form-input end-time-input"
                                disabled
                                value={endTimeDisplay}
                              />

                              <button
                                type="button"
                                className="delete-row-btn"
                                onClick={() => removeDraftRow(idx)}
                                disabled={
                                  !!editId ||
                                  (form.appointmentsDraft || []).length === 1
                                }
                              >
                                ✕
                              </button>
                            </div>
                          );
                        })}
                      </div>

                      <button
                        type="button"
                        className="add-row-btn"
                        onClick={addDraftRow}
                        style={{ marginTop: "10px" }}
                        disabled={!!editId}
                      >
                        + Add another date
                      </button>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Recurrence <span className="req-star">*</span>
                      </label>
                      <select
                        className="form-select"
                        value={form.recurrence}
                        onChange={(e) => handleRecurrenceChange(e.target.value)}
                      >
                        {recurrences
                          .filter((r) => r !== "All")
                          .map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                      </select>
                      {fieldErrors.recurrence && (
                        <div className="error-msg">
                          {fieldErrors.recurrence}
                        </div>
                      )}
                    </div>

                    <div
                      className="form-group span-full"
                      style={{ position: "relative" }}
                    >
                      <label className="form-label">
                        Address <span className="req-star">*</span>
                      </label>
                      <div className="input-icon-wrapper">
                        <div className="input-icon"></div>
                        <input
                          type="text"
                          className="form-input"
                          value={form.address}
                          onChange={(e) => {
                            const val = e.target.value;
                            handleFormChange("address", val);
                            setAddressQuery(val);
                            addressQueryRef.current = val;
                          }}
                          onFocus={() => {
                            if (
                              form.address &&
                              form.address.trim().length > 0
                            ) {
                              loadAddressSuggestions(form.address);
                            }
                          }}
                          placeholder="Enter full address..."
                        />
                      </div>
                      {form.address &&
                        form.clientId &&
                        (() => {
                          const client = clientsList.find(
                            (c) => (c.id || c._id) === form.clientId,
                          );
                          const clientAddr = buildClientAddress(client);
                          return clientAddr && form.address === clientAddr;
                        })() && (
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#64748b",
                              marginTop: "4px",
                              fontStyle: "italic",
                            }}
                          >
                            Address auto-filled from client profile
                          </div>
                        )}
                      {addressSuggestions.length > 0 && (
                        <div className="suggestions-dropdown">
                          {addressSuggestions.map((sug, idx) => (
                            <div
                              key={`${sug.lat}-${sug.lon}-${idx}`}
                              className="suggestion-row"
                              onClick={() => {
                                handleFormChange("address", sug.display_name);
                                handleFormChange("lat", sug.lat);
                                handleFormChange("lng", sug.lon);
                                setAddressSuggestions([]);
                                setAddressQuery("");
                                addressQueryRef.current = "";
                              }}
                            >
                              {sug.display_name}
                            </div>
                          ))}
                        </div>
                      )}
                      {fieldErrors.address && (
                        <div className="error-msg">{fieldErrors.address}</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* --- ADDITIONAL DETAILS SECTION --- */}
                <div className="additional-section">
                  <div className="additional-header">
                    <div className="additional-title-row">
                      <div className="additional-icon-wrap">
                        <FontAwesomeIcon icon={faClipboardCheck} />
                      </div>
                      <div>
                        <div className="additional-title">
                          Additional Details
                        </div>
                        <div className="additional-subtitle">
                          Configure your visit settings
                        </div>
                      </div>
                    </div>
                    <div className="additional-divider" />
                  </div>

                  <div className="additional-grid">
                    <div className="additional-card">
                      <div
                        className="card-heading"
                        style={{ justifyContent: "space-between", width: "100%" }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            minWidth: 0,
                          }}
                        >
                          <div className="card-icon">
                            <FontAwesomeIcon icon={faClipboardList} />
                          </div>
                          <div className="card-text">
                            <div className="card-title">Check-in Questions</div>
                            <div className="card-desc">
                              Show check-in set in mobile
                            </div>
                          </div>
                        </div>
                        <div className="toggle-wrap">
                          <div
                            className={`toggle-switch ${
                              (form.showCheckInQuestions ?? true) ? "on" : "off"
                            }`}
                            role="switch"
                            aria-checked={form.showCheckInQuestions ?? true}
                            tabIndex={0}
                            onClick={() =>
                              handleFormChange(
                                "showCheckInQuestions",
                                !(form.showCheckInQuestions ?? true),
                              )
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleFormChange(
                                  "showCheckInQuestions",
                                  !(form.showCheckInQuestions ?? true),
                                );
                              }
                            }}
                          >
                            <span className="toggle-thumb" />
                          </div>
                        </div>
                      </div>
                      {(form.showCheckInQuestions ?? true) ? (
                        <>
                          <SearchableSelect
                            className="styled-select"
                            options={checkInOptions}
                            value={checkInQuestionSetId}
                            placeholder="Select check-in set"
                            onChange={(val) => setCheckInQuestionSetId(val)}
                          />
                          {fieldErrors.checkInQuestionSetId && (
                            <div className="error-msg">
                              {fieldErrors.checkInQuestionSetId}
                            </div>
                          )}
                        </>
                      ) : null}
                    </div>

                    <div className="additional-card">
                      <div
                        className="card-heading"
                        style={{ justifyContent: "space-between", width: "100%" }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            minWidth: 0,
                          }}
                        >
                          <div className="card-icon">
                            <FontAwesomeIcon icon={faClipboardList} />
                          </div>
                          <div className="card-text">
                            <div className="card-title">Check-out Questions</div>
                            <div className="card-desc">
                              Show check-out set in mobile

                            </div>
                          </div>
                        </div>
                        <div className="toggle-wrap">
                          <div
                            className={`toggle-switch ${
                              (form.showCheckOutQuestions ?? true) ? "on" : "off"
                            }`}
                            role="switch"
                            aria-checked={form.showCheckOutQuestions ?? true}
                            tabIndex={0}
                            onClick={() =>
                              handleFormChange(
                                "showCheckOutQuestions",
                                !(form.showCheckOutQuestions ?? true),
                              )
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleFormChange(
                                  "showCheckOutQuestions",
                                  !(form.showCheckOutQuestions ?? true),
                                );
                              }
                            }}
                          >
                            <span className="toggle-thumb" />
                          </div>
                        </div>
                      </div>
                      {(form.showCheckOutQuestions ?? true) ? (
                        <>
                          <SearchableSelect
                            className="styled-select"
                            options={checkOutOptions}
                            value={checkOutQuestionSetId}
                            placeholder="Select check-out set"
                            onChange={(val) => setCheckOutQuestionSetId(val)}
                          />
                          {fieldErrors.checkOutQuestionSetId && (
                            <div className="error-msg">
                              {fieldErrors.checkOutQuestionSetId}
                            </div>
                          )}
                        </>
                      ) : null}
                    </div>

                    <div className="additional-card toggle-card">
                      <div className="card-heading">
                        <div className="card-icon">
                          <FontAwesomeIcon icon={faRobot} />
                        </div>
                        <div className="card-text">
                          <div className="card-title">Shift Report</div>
                          <div className="card-desc">Enable AI assistant</div>
                        </div>
                      </div>
                      <div className="toggle-wrap">
                        <div
                          className={`toggle-switch ${
                            (form.showAgentAlbert ?? true) ? "on" : "off"
                          }`}
                          role="switch"
                          aria-checked={form.showAgentAlbert ?? true}
                          tabIndex={0}
                          onClick={() =>
                            handleFormChange(
                              "showAgentAlbert",
                              !(form.showAgentAlbert ?? true),
                            )
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleFormChange(
                                "showAgentAlbert",
                                !(form.showAgentAlbert ?? true),
                              );
                            }
                          }}
                        >
                          <span className="toggle-thumb" />
                        </div>
                      </div>
                    </div>

                    <div className="additional-card toggle-card">
                      <div className="card-heading">
                        <div className="card-icon">
                          <FontAwesomeIcon icon={faMicrophone} />
                        </div>
                        <div className="card-text">
                          <div className="card-title">Voice Analysis</div>
                          <div className="card-desc">
                            Collect voice biomarkers
                          </div>
                        </div>
                      </div>
                      {voiceProvisionWarning ? (
                        <div
                          style={{
                            marginTop: "8px",
                            fontSize: "13px",
                            color: "#b45309",
                            lineHeight: 1.3,
                          }}
                        >
                          {voiceProvisionWarning}
                        </div>
                      ) : null}
                      <div className="toggle-wrap">
                        <div
                          className={`toggle-switch ${
                            (form.showVoiceAnalysis ?? true) ? "on" : "off"
                          }`}
                          role="switch"
                          aria-checked={form.showVoiceAnalysis ?? true}
                          tabIndex={0}
                          onClick={() =>
                            handleFormChange(
                              "showVoiceAnalysis",
                              !(form.showVoiceAnalysis ?? true),
                            )
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleFormChange(
                                "showVoiceAnalysis",
                                !(form.showVoiceAnalysis ?? true),
                              );
                            }
                          }}
                        >
                          <span className="toggle-thumb" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="additional-card notes-card">
                    <div className="card-heading">
                      <div className="card-icon">
                        <FontAwesomeIcon icon={faNotesMedical} />
                      </div>
                      <div className="card-text">
                        <div className="card-title">Notes (Optional)</div>
                        <div className="card-desc">
                          Any special instructions or gate codes
                        </div>
                      </div>
                    </div>
                    <textarea
                      className="form-textarea"
                      value={form.notes}
                      onChange={(e) =>
                        handleFormChange("notes", e.target.value)
                      }
                      placeholder="Any special instructions or gate codes..."
                    ></textarea>
                  </div>
                </div>
                {error && <div className="modal-error">{error}</div>}
              </div>
            </div>

            <div className="modal-fixed-footer">
              <button
                className="ghost-btn"
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="primary-btn"
                onClick={handleSave}
                disabled={loading || !isFormValid}
              >
                {editId ? "Save Changes" : "Create Appointment"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Check-in Answers Modal - REDESIGNED */}
      {isCheckInModalOpen && (
        <div
          className="appt-modal-overlay"
          onClick={() => setIsCheckInModalOpen(false)}
        >
          <div
            className="appt-modal answer-modal-card theme-checkin"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "600px" }}
          >
            <div className="answer-header">
              <div className="answer-header-left">
                <div className="answer-icon-box">
                  <FontAwesomeIcon icon={faCheckCircle} />
                </div>
                <div className="answer-title">
                  <h2>Check-in Report</h2>
                  <div className="answer-date">
                    {activeAppointment?.start
                      ? dayjs(activeAppointment.start).format(
                          "YYYY/DD/MM • h:mm A",
                        )
                      : "Date N/A"}
                  </div>
                </div>
              </div>
              <button
                className="icon-button"
                onClick={() => setIsCheckInModalOpen(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="answer-body">
              {MOCK_CHECK_IN.map((item, idx) => (
                <div className="qa-item" key={idx}>
                  <div className="qa-question">{item.question}</div>
                  <div className="qa-answer">
                    <span className="answer-pill highlight">{item.answer}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Check-out Answers Modal - REDESIGNED */}
      {isCheckOutModalOpen && (
        <div
          className="appt-modal-overlay"
          onClick={() => setIsCheckOutModalOpen(false)}
        >
          <div
            className="appt-modal answer-modal-card theme-checkout"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "600px" }}
          >
            <div className="answer-header">
              <div className="answer-header-left">
                <div className="answer-icon-box">
                  <FontAwesomeIcon icon={faSignOutAlt} />
                </div>
                <div className="answer-title">
                  <h2>Check-out Report</h2>
                  <div className="answer-date">
                    {activeAppointment?.end
                      ? dayjs(activeAppointment.end).format(
                          "YYYY/DD/MM • h:mm A",
                        )
                      : "Date N/A"}
                  </div>
                </div>
              </div>
              <button
                className="icon-button"
                onClick={() => setIsCheckOutModalOpen(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="answer-body">
              {MOCK_CHECK_OUT.map((item, idx) => (
                <div className="qa-item" key={idx}>
                  <div className="qa-question">{item.question}</div>
                  <div className="qa-answer">
                    {Array.isArray(item.answer) ? (
                      item.answer.map((tag, i) => (
                        <span key={i} className="answer-pill">
                          {tag}
                        </span>
                      ))
                    ) : (
                      <span className="answer-pill highlight">
                        {item.answer}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal - REDESIGNED */}
      {isDeleteModalOpen && deleteTarget && (
        <div
          className="appt-modal-overlay"
          onClick={() => setIsDeleteModalOpen(false)}
        >
          <div
            className="appt-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "480px" }}
          >
            <div className="delete-alert-wrapper">
              <div className="delete-icon-large">
                <FontAwesomeIcon icon={faTrash} />
              </div>
              <div className="delete-title">Delete Client Schedule?</div>
              <div className="delete-desc">
                This action cannot be undone. This will permanently remove the
                appointment for <strong>{deleteTarget.clientName}</strong> from
                the schedule.
              </div>

              <div className="delete-card-preview">
                <div
                  style={{
                    fontSize: "12px",
                    color: "#64748b",
                    marginBottom: "4px",
                  }}
                >
                  APPOINTMENT DETAILS
                </div>
                <div style={{ fontWeight: 700, color: "#0f172a" }}>
                  {deleteTarget.clientName || "Unknown Client"}
                </div>
                <div style={{ fontSize: "13px", color: "#334155" }}>
                  {dayjs(deleteTarget.start).format("dddd, MMM D • h:mm A")}
                </div>
              </div>

              <div className="delete-actions">
                <button
                  className="btn-cancel-large"
                  onClick={() => setIsDeleteModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn-delete-large"
                  onClick={async () => {
                    try {
                      setLoading(true);
                      setError("");
                      await deleteAppointment(
                        deleteTarget.id || deleteTarget._id,
                      );
                      const filters = {
                        status: statusFilter,
                        providerRole: providerFilter,
                        recurrence: recurrenceFilter,
                        q: search || undefined,
                        locationId: activeLocation?.id || activeLocation?._id,
                      };
                      const payload = await fetchAppointments({
                        ...filters,
                        includeStats: true,
                      });
                      const data = Array.isArray(payload)
                        ? payload
                        : payload?.appointments || [];
                      setServerStats(
                        Array.isArray(payload) ? null : payload?.stats || null,
                      );
                      setAppointments(data);
                      if (data && data.length > 0) {
                        setActiveAppointment(data[0]);
                      } else {
                        setActiveAppointment(null);
                      }
                    } catch (err) {
                      setError(err.message || "Failed to delete appointment");
                    } finally {
                      setLoading(false);
                      setIsDeleteModalOpen(false);
                      setDeleteTarget(null);
                    }
                  }}
                >
                  Delete Appointment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Appointments;
